/* eslint-disable react/jsx-no-constructed-context-values */

'use client'

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from 'react'
import { useRouter } from 'next/navigation'
import { CartItemsData } from '@models'
import { BackDropLoader } from '@components/common/BackDropLoader'
import {
  addItemsToCart,
  getAllCartsItem,
  getHomeCollectionCharges,
  getItemInclusions,
  removeItemFromCart,
  submitAbandonedCartLead,
} from '@utils/api/dashboard'
import { useSnackbar } from '../alerts-state-management'
import { useCheckoutInfo } from '../checkout-state-management'
import { useUserState } from '../login-state-management'

// TODO: Remove ForceAny from here

type CartContextType = {
  cartItems: CartItemsData[]
  addNewItem: (item: CartItemsData) => Promise<boolean>
  removeCartItem: (itemId: number, cityId: number, labId: number) => void
  loading: boolean
  homeCollectionCharges: number
  totalPrice: number
  clearCartState: () => void
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const useCart = () => {
  const context = useContext(CartContext)

  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

type CartProviderProps = {
  children: ReactNode
}

export type CartData = CartItemsData[]

const CRM_LEAD_DELAY_MS = 60 * 60 * 1000

// TODO: Changes these all callback hell
export function CartProvider({ children }: CartProviderProps) {
  const { userDetails } = useUserState()
  const { showSnackbar } = useSnackbar()
  const router = useRouter()
  const [totalPrice, setTotalPrice] = useState(0)
  const [cartItems, setCartItems] = useState<CartData>([])
  const [pendingItems, setPendingItems] = useState<CartData>([])
  const [loading, setLoading] = useState(false)
  const { setCheckoutInfo, checkoutInfo, setDeliveryCharge } = useCheckoutInfo()
  const [homeCollectionCharges, setHomeCollectionCharges] = useState(0)

  const clearCartState = useCallback(() => {
    setCartItems([])
    setPendingItems([])
    setCheckoutInfo({})
    setTotalPrice(0)
    setHomeCollectionCharges(0)
    setDeliveryCharge(0)
  }, [setCheckoutInfo, setDeliveryCharge])

  const refetchAllCartItems = () => {
    setLoading(true)
    if (userDetails && userDetails.phoneNumber)
      getAllCartsItem(userDetails.phoneNumber)
        .then((data) => {
          setCartItems(data.data)
          setLoading(false)
        })
        .catch((error) => {
          showSnackbar(error.message, 'error')
          setLoading(false)
        })
  }

  const getItemId = (item: CartItemsData) =>
    Number(item.itemid || item.itemId || 0)

  const getItemName = (item: CartItemsData) =>
    String(item.itemname || item.heading || 'This item')

  const getCartConflictMessage = async (newItem: CartItemsData) => {
    const newItemId = getItemId(newItem)
    if (!newItemId || !cartItems.length) return null

    if (cartItems.some((cartItem) => getItemId(cartItem) === newItemId)) {
      return `${getItemName(newItem)} is already in your cart.`
    }

    const cartItemIds = cartItems.map(getItemId).filter(Boolean)
    const itemIds = [...new Set([...cartItemIds, newItemId])]

    try {
      const inclusionResponse = await getItemInclusions(itemIds.join(','))
      const inclusions = inclusionResponse.data
      const newPackageTests = inclusions.filter(
        (inclusion) => Number(inclusion.PackageID) === newItemId
      )

      if (newPackageTests.length) {
        const includedTestIds = new Set(
          newPackageTests.map((inclusion) => Number(inclusion.itemid))
        )
        const includedCartTest = cartItems.find((cartItem) => {
          const cartItemId = getItemId(cartItem)
          const cartItemIsPackage = inclusions.some(
            (inclusion) => Number(inclusion.PackageID) === cartItemId
          )
          return includedTestIds.has(cartItemId) && !cartItemIsPackage
        })

        if (includedCartTest) {
          return `Remove ${getItemName(includedCartTest)} from your cart before adding ${getItemName(newItem)}.`
        }
      }

      const containingPackage = cartItems.find((cartItem) => {
        const cartItemId = getItemId(cartItem)
        return inclusions.some(
          (inclusion) =>
            Number(inclusion.PackageID) === cartItemId &&
            Number(inclusion.itemid) === newItemId
        )
      })

      if (containingPackage) {
        return `${getItemName(newItem)} is already included in ${getItemName(containingPackage)}.`
      }

      const getInclusionRows = (itemId: number) => {
        const packageRows = inclusions.filter(
          (inclusion) => Number(inclusion.PackageID) === itemId
        )
        if (packageRows.length) return packageRows
        return inclusions.filter(
          (inclusion) =>
            inclusion.ItemType === 'Test' && Number(inclusion.itemid) === itemId
        )
      }
      const newItemRows = getInclusionRows(newItemId)

      let duplicateComponentNames: string[] = []
      const conflictingCartItem = cartItems.find((cartItem) => {
        const cartItemRows = getInclusionRows(getItemId(cartItem))
        duplicateComponentNames = newItemRows.flatMap((newRow) => {
          const newParameters = new Map(
            newRow.TestParametersObj.filter(
              (parameter) => parameter.DuplicateAllowed !== '1'
            ).map((parameter) => [
              parameter.ParameterID,
              parameter.ParameterName,
            ])
          )
          return cartItemRows.flatMap((cartRow) =>
            cartRow.TestParametersObj.filter(
              (parameter) =>
                parameter.DuplicateAllowed !== '1' &&
                newParameters.has(parameter.ParameterID)
            ).map((parameter) => parameter.ParameterName)
          )
        })
        duplicateComponentNames = [...new Set(duplicateComponentNames)]
        return duplicateComponentNames.length > 0
      })

      if (conflictingCartItem) {
        const componentNames = duplicateComponentNames.slice(0, 3).join(', ')
        return `${getItemName(newItem)} has duplicate components (${componentNames}) already present in ${getItemName(conflictingCartItem)}.`
      }
    } catch {
      // Do not block cart actions when the external inclusion service is down.
    }

    return null
  }

  const addNewItem = async (item: CartItemsData) => {
    setLoading(true)
    if (!userDetails?.cityId) {
      showSnackbar('Please select a city first', 'warning')
      setLoading(false)
      return false
    }
    const conflictMessage = await getCartConflictMessage(item)
    if (conflictMessage) {
      showSnackbar(conflictMessage, 'warning')
      setLoading(false)
      return false
    }
    if (userDetails.accessToken) {
      try {
        await addItemsToCart([item.itemId])
        if (pendingItems.length > 0) {
          const pendingIds = pendingItems.map((pendingItem) => pendingItem.id)
          await addItemsToCart(pendingIds)
          setPendingItems([])
          router.refresh()
        }
        refetchAllCartItems()
      } catch {
        setLoading(false)
        setPendingItems((prev) => [...prev, item])
      }
    } else {
      setLoading(false)
      setCartItems((prev) => [...prev, item])
      setPendingItems((prev) => [...prev, item])
    }
    return true
  }

  const removeCartItem = (itemId: number, cityId: number, labId: number) => {
    setLoading(true)
    setCartItems((currentItems) =>
      currentItems.filter((item) => getItemId(item) !== Number(itemId))
    )
    setPendingItems((currentItems) =>
      currentItems.filter((item) => getItemId(item) !== Number(itemId))
    )
    if (checkoutInfo?.patients?.length) {
      const updatedPatients = checkoutInfo.patients.map((patient) => {
        const updatedCartItems = patient.cartItems.filter(
          (item) => item.id !== itemId
        )
        return {
          ...patient,
          cartItems: updatedCartItems,
        }
      })
      setCheckoutInfo({
        ...checkoutInfo,
        patients: updatedPatients,
      })
    }
    if (userDetails?.accessToken) {
      removeItemFromCart(itemId.toString(), cityId, labId)
        .then(() => {
          refetchAllCartItems()
          showSnackbar('Item removed from cart', 'success')
        })
        .catch(() => {
          showSnackbar('Error removing item from cart', 'error')
          refetchAllCartItems()
        })
    } else {
      const newCartItems = cartItems.filter((item) => item.itemId !== itemId)
      setCartItems(newCartItems)
    }
    setLoading(false)
  }

  useEffect(() => {
    setLoading(true)
    if (userDetails && userDetails.phoneNumber) {
      getAllCartsItem(userDetails.phoneNumber)
        .then((data) => {
          setCartItems(data.data)
          if (pendingItems.length > 0) {
            const pendingIds = pendingItems.map((item) => item.itemId)
            addItemsToCart(pendingIds)
              .then(() => {
                setPendingItems([])
                setLoading(false)
                refetchAllCartItems()
                router.push('/cart')
              })
              .catch((error) => {
                showSnackbar(error.message, 'error')
                setLoading(false)
              })
          }
          setLoading(false)
        })
        .catch((error) => {
          showSnackbar(error.message, 'error')
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, [userDetails])

  useEffect(() => {
    if (cartItems.length === 0) {
      return
    }
    setCheckoutInfo({})
    setTotalPrice(
      cartItems.reduce(
        (acc, item) => (item.price ? acc + item.price : acc + item.rate),
        0
      )
    )
    if (userDetails?.accessToken && cartItems.length > 0)
      getHomeCollectionCharges({
        ItemID: cartItems.map((obj) => obj.itemid).join(','),
        TotalBillAmount: totalPrice.toString(),
        StateId: '1',
        minMaxRadius: '2',
      })
        .then((data) => {
          setHomeCollectionCharges(Number(data.data[0].Amount || 0))
          setDeliveryCharge(Number(data.data[0].Amount || 0))
        })
        .catch(() => {
          // showSnackbar(error.message, 'error')
        })
  }, [cartItems])

  useEffect(() => {
    const mobileNumber = userDetails?.phoneNumber
    if (!mobileNumber || !cartItems.length) return undefined

    const itemIds = cartItems
      .map((item) => item.itemid || item.itemId || item.id)
      .filter(Boolean)
      .sort()
      .join(',')
    const storageKey = `apollo-abandoned-cart:${mobileNumber}:${itemIds}`
    if (localStorage.getItem(storageKey)) return undefined

    const submitLead = () => {
      void submitAbandonedCartLead(mobileNumber)
        .then((result) => {
          if (result.submitted || result.reason === 'Lead already submitted') {
            localStorage.setItem(storageKey, new Date().toISOString())
          }
        })
        .catch(() => undefined)
    }
    const timeout = window.setTimeout(submitLead, CRM_LEAD_DELAY_MS)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [cartItems, userDetails?.phoneNumber])

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addNewItem,
        removeCartItem,
        loading,
        homeCollectionCharges,
        totalPrice,
        clearCartState,
      }}
    >
      <BackDropLoader open={loading} />

      {children}
    </CartContext.Provider>
  )
}
